import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';

// Import scroll utility
import { smoothScrollToElement } from './utils/smoothScroll';

// Import all components
import {
  Navbar,
  HeroSection,
  AboutSection,
  FeaturesSection,
  HowItWorksSection,
  BenefitsSection,
  UseCasesSection,
  TestimonialsSection,
  PricingSection,
  IntegrationsSection,
  BlogSection,
  TeamSection,
  ContactSection,
  Footer,
} from './components';

// Import policy pages
import {
  PrivacyPolicy,
  TermsOfService,
  Security,
  GDPR,
  CookiePolicy,
  ProductPage,
  BlogCategoryPage,
  HowItWorksPage,
  PricingPage,
  TestimonialsPage,
} from './pages';

import CookieBanner from './components/shared/CookieBanner';
import ScrollToTop from './components/shared/ScrollToTop';

// Home Page Component
const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle scroll to section from state or hash
    const hash = location.hash.replace('#', '');
    const stateScrollTarget = location.state?.scrollToSection;
    const scrollTarget = stateScrollTarget || hash;
    
    let scrollTimeout;
    let retryTimeout;
    let finalRetryTimeout;
    
    if (scrollTarget) {
      // Give the page time to fully render before scrolling
      scrollTimeout = setTimeout(() => {
        const element = document.getElementById(scrollTarget);
        if (element) {
          smoothScrollToElement(scrollTarget, 100);
          // Clear the state after successful scroll to avoid repeated re-triggering
          if (stateScrollTarget && typeof window !== 'undefined') {
            window.history.replaceState({}, '', window.location.pathname);
          }
        } else {
          // If element not found, try again after a bit longer
          retryTimeout = setTimeout(() => {
            const retryElement = document.getElementById(scrollTarget);
            if (retryElement) {
              smoothScrollToElement(scrollTarget, 100);
              if (stateScrollTarget && typeof window !== 'undefined') {
                window.history.replaceState({}, '', window.location.pathname);
              }
            } else {
              // Final retry with longer delay
              finalRetryTimeout = setTimeout(() => {
                smoothScrollToElement(scrollTarget, 100);
                if (stateScrollTarget && typeof window !== 'undefined') {
                  window.history.replaceState({}, '', window.location.pathname);
                }
              }, 200);
            }
          }, 300);
        }
      }, 200);
    }
    
    // Cleanup timeouts
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);
      if (finalRetryTimeout) clearTimeout(finalRetryTimeout);
    };
  }, [location, navigate]);

  return (
    <div className="bg-dark-base min-h-screen">
      <Navbar />
      <HeroSection />
      <AboutSection />
      <FeaturesSection />
      <HowItWorksSection />
      <BenefitsSection />
      <UseCasesSection />
      <TestimonialsSection />
      <PricingSection />
      <IntegrationsSection />
      <BlogSection />
      <TeamSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/security" element={<Security />} />
        <Route path="/gdpr" element={<GDPR />} />
        <Route path="/cookie" element={<CookiePolicy />} />
        <Route path="/veritonetech-ai" element={<ProductPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/product" element={<Navigate to="/veritonetech-ai" replace />} />
        <Route path="/blog/:category" element={<BlogCategoryPage />} />
      </Routes>
      <CookieBanner />
    </Router>
  );
}

export default App;
